import React, { useState, useEffect } from "react";
import { Form, Button, Dropdown, Badge, Collapse } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { IntelligentSearchService } from "../../services/IntelligentSearchService";
import "./SemanticSearch.css";

const JobRequirementsForm = ({
  onSubmit,
  isLoading,
  searchHistory,
  onClearHistory,
  onRestoreSearch
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    jobRequirements: "",
    dataSource: 0,
    maxCandidates: 5, // Add default value
    additionalContext: ""
  });
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        name === "dataSource" || name === "maxCandidates"
          ? Number(value)
          : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.jobRequirements.trim()) {
      onSubmit(formData);
    }
  };

  const handleRestoreHistoryItem = (historyItem) => {
    setFormData({
      jobRequirements: historyItem.jobRequirements,
      company: historyItem.company || "",
      dataSource: historyItem.dataSource || 0,
      additionalContext: ""
    });
    onRestoreSearch(historyItem);
  };

  const getCharacterCount = (text, max) => {
    const count = text.length;
    const percentage = (count / max) * 100;
    let variant = "primary";

    if (percentage > 90) variant = "danger";
    else if (percentage > 75) variant = "warning";

    return { count, variant };
  };

  const jobReqCount = getCharacterCount(formData.jobRequirements, 10000);
  const contextCount = getCharacterCount(formData.additionalContext, 2000);

  return (
    <div className="job-requirements-form">
      <Form onSubmit={handleSubmit}>
        <Form.Group className="mb-3">
          <Form.Label className="form-label-required">
            {t("Job Requirements")}
            <Badge bg={jobReqCount.variant} className="ms-2 character-counter">
              {jobReqCount.count}/10000
            </Badge>
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={10}
            name="jobRequirements"
            value={formData.jobRequirements}
            onChange={handleInputChange}
            placeholder={t(
              "Describe the role, required skills, and experience..."
            )}
            required
            maxLength={10000}
            className="job-requirements-textarea"
          />
          <Form.Text className="text-dark">
            {t("Be specific about skills, experience level, and requirements")}
          </Form.Text>
        </Form.Group>
        <Form.Group className="mb-4">
          <Form.Label>
            {t("Additional Context")}
            <Badge bg={contextCount.variant} className="ms-2 character-counter">
              {contextCount.count}/2000
            </Badge>
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={4}
            name="additionalContext"
            value={formData.additionalContext}
            onChange={handleInputChange}
            placeholder={t(
              "Optional: team dynamics, company culture, specific projects..."
            )}
            maxLength={2000}
          />
          <Form.Text className="text-dark">
            {t("Optional context to help improve matching accuracy")}
          </Form.Text>
        </Form.Group>
        <div className="row mb-3">
          <div className="col-md-6">
            <Form.Group>
              <Form.Label>{t("Data Source")}</Form.Label>
              <Form.Control
                as="select"
                name="dataSource"
                value={formData.dataSource || 0}
                onChange={handleInputChange}
              >
                {["Our Database", "CV Files"].map((num, index) => (
                  <option key={num} value={Number(index)}>
                    {num}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          </div>
          <div className="col-md-6">
            <Form.Group>
              <Form.Label>{t("Max Candidates")}</Form.Label>
              <Form.Control
                as="select"
                name="maxCandidates"
                value={formData.maxCandidates || 5}
                onChange={handleInputChange}
              >
                {["5", "10", "15", "20"].map((num) => (
                  <option key={num} value={Number(num)}>
                    {num}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          </div>
        </div>

        <div className="form-actions">
          <Button
            type="submit"
            variant="primary"
            disabled={isLoading || !formData.jobRequirements}
            className="search-button"
          >
            {isLoading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                {t("Searching...")}
              </>
            ) : (
              <>
                <i className="fas fa-search me-2"></i>
                {t("Search")}
              </>
            )}
          </Button>
        </div>
      </Form>

      {searchHistory.length > 0 && (
        <div className="search-history mt-4">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6
              className="history-title d-flex align-items-center cursor-pointer"
              onClick={() => setShowHistory(!showHistory)}
              style={{ cursor: "pointer" }}
            >
              <i className="fas fa-history me-2"></i>
              {t("Search History")}
              <i
                className={`fas fa-chevron-${showHistory ? "up" : "down"} ms-2`}
              ></i>
            </h6>
            <Dropdown>
              <Dropdown.Toggle
                variant="outline-secondary"
                size="sm"
                className="history-actions-btn"
              >
                <i className="fas fa-ellipsis-v"></i>
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={onClearHistory}>
                  <i className="fas fa-trash me-2"></i>
                  {t("Clear History")}
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>

          <Collapse in={showHistory}>
            <div className="history-list">
              {searchHistory.slice(0, 5).map((item) => (
                <div
                  key={item.id}
                  className="history-item"
                  onClick={() => handleRestoreHistoryItem(item)}
                >
                  <div className="history-content">
                    <div className="history-text">
                      {item.jobRequirements.substring(0, 60)}
                      {item.jobRequirements.length > 60 && "..."}
                    </div>
                    <div className="history-meta">
                      <span className="history-date">
                        {new Date(item.timestamp).toLocaleDateString()}
                      </span>
                      <span className="history-results">
                        {item.resultsCount} {t("results")}
                      </span>
                    </div>
                  </div>
                  <i className="fas fa-redo history-restore-icon"></i>
                </div>
              ))}
            </div>
          </Collapse>
        </div>
      )}
    </div>
  );
};

export default JobRequirementsForm;
